import {
  YES_NO,
  applicantSection,
  deMinimisAttachments,
  otherAttachments,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * E3. Restrukturyzacja lub rozłożenie na raty zadłużenia czynszowego — art. 59 ust. 1–3 ustawy z 27.08.2009 r.
 * o finansach publicznych (ulgi w należnościach cywilnoprawnych na zasadach z uchwały rady gminy). Wynik — porozumienie
 * (ugoda) cywilnoprawne, nie decyzja. Brak wzoru ogólnopolskiego; układ jak wzór Częstochowy F-BGLK0141 „Wniosek
 * o udzielenie ulgi w spłacie zadłużenia” (https://bip.czestochowa.pl/attachments/download/98652, pobrany 22.10.2026:
 * rodzaj ulgi, gospodarstwo domowe, uznanie długu, dochody, zobowiązanie do terminowych wpłat) i wzór Grębocic
 * (https://bip-v1-files.idcom-web.pl/sites/46267/cms/szablony/13208/pliki/wniosek_o_rozlozenie_na_raty_zaleglosci_za_czynsz.pdf,
 * pobrany 22.10.2026: proponowana rata i termin, dochody netto osób w lokalu); pominięto część „wypełnia pracownik”.
 */
const INSTALMENTS: EbiCondition = { field: "rodzajUlgi", includes: "raty" };
const DEFERRAL: EbiCondition = { field: "rodzajUlgi", includes: "odroczenie" };
const ENTREPRENEUR: EbiCondition = { field: "przedsiebiorca", equals: "tak" };

const E3: IEbiFormDefinition = {
  code: "E3",
  version: 1,
  title: "Wniosek o udzielenie ulgi w spłacie zadłużenia z tytułu opłat za lokal",
  category: EEbiFormCategory.Housing,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Rozłożenie na raty, odroczenie terminu płatności albo umorzenie zaległości czynszowych wobec gminy.",
  keywords: ["zadłużenie", "zaległości czynszowe", "raty", "odroczenie", "umorzenie", "czynsz", "ugoda"],
  organ: "Wójt / Burmistrz / Prezydent Miasta albo kierownik jednostki zarządzającej zasobem (np. ZBiLK, ZGM) — według uchwały rady gminy",
  legalBasis:
    "art. 59 ust. 1–3 ustawy z dnia 27 sierpnia 2009 r. o finansach publicznych; uchwała rady gminy określająca zasady umarzania, odraczania i rozkładania na raty należności pieniężnych o charakterze cywilnoprawnym",
  request: "Wnoszę o udzielenie ulgi w spłacie zadłużenia z tytułu opłat za używanie lokalu mieszkalnego w zakresie wskazanym poniżej.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty. Pełnomocnictwo — 17 zł opłaty skarbowej.",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "zadluzenie",
      title: "Lokal i zadłużenie",
      fields: [
        {
          code: "adresLokalu",
          type: "text",
          label: "Adres lokalu, którego dotyczy zadłużenie",
          hint: "Także wtedy, gdy jest to Twój adres zamieszkania.",
          required: true,
          maxLength: 300,
        },
        { code: "kwotaZadluzenia", type: "number", label: "Kwota zadłużenia [zł] (jeśli ją znasz)", width: "half", decimals: 2, min: 0 },
        { code: "stanNaDzien", type: "date", label: "Według stanu na dzień", width: "half" },
      ],
    },
    {
      code: "ulga",
      title: "Wnoszę o udzielenie ulgi",
      fields: [
        {
          code: "rodzajUlgi",
          type: "checkboxes",
          label: "Rodzaj ulgi",
          required: true,
          options: [
            { value: "raty", label: "rozłożenie zaległości na raty" },
            { value: "odroczenie", label: "odroczenie terminu płatności" },
            { value: "umorzenieOdsetek", label: "umorzenie odsetek po całkowitej spłacie zadłużenia" },
            { value: "umorzenie", label: "umorzenie zaległości w całości lub w części" },
          ],
        },
        { code: "liczbaRat", type: "number", label: "Proponowana liczba rat", required: true, visibleWhen: INSTALMENTS, width: "third", min: 2, max: 120 },
        { code: "kwotaRaty", type: "number", label: "Proponowana miesięczna rata [zł]", visibleWhen: INSTALMENTS, width: "third", decimals: 2, min: 1 },
        { code: "dzienPlatnosci", type: "number", label: "Rata płatna do dnia każdego miesiąca", visibleWhen: INSTALMENTS, width: "third", min: 1, max: 31 },
        { code: "terminOdroczenia", type: "date", label: "Proponowany termin płatności", required: true, visibleWhen: DEFERRAL, width: "half" },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Uzasadnienie wniosku",
          hint: "Opisz, co spowodowało zadłużenie i jak zamierzasz je spłacić.",
          required: true,
          maxLength: 5000,
        },
      ],
    },
    {
      code: "sytuacja",
      title: "Sytuacja materialna gospodarstwa domowego",
      fields: [
        { code: "liczbaOsob", type: "number", label: "Liczba osób w gospodarstwie domowym", required: true, width: "half", min: 1, max: 30 },
        { code: "liczbaPelnoletnich", type: "number", label: "w tym liczba osób pełnoletnich", required: true, width: "half", min: 1, max: 30 },
        {
          code: "dochody",
          type: "repeat",
          label: "Średniomiesięczne dochody netto moje i osób zamieszkujących ze mną za 3 miesiące poprzedzające złożenie wniosku",
          itemLabel: "Dochód",
          required: true,
          min: 1,
          max: 30,
          fields: [
            { code: "osoba", type: "text", label: "Imię i nazwisko", required: true, width: "third", maxLength: 200 },
            { code: "zrodlo", type: "text", label: "Źródło dochodu", required: true, width: "third", maxLength: 200, placeholder: "np. wynagrodzenie, emerytura" },
            { code: "kwota", type: "number", label: "Dochód netto [zł]", required: true, width: "third", decimals: 2, min: 0 },
          ],
        },
        {
          code: "dochodNaOsobe",
          type: "number",
          label: "Średniomiesięczny dochód netto na osobę w gospodarstwie domowym [zł]",
          required: true,
          width: "half",
          decimals: 2,
          min: 0,
        },
        {
          code: "bezDochodu",
          type: "radio",
          label: "Czy któraś z pełnoletnich osób w gospodarstwie domowym nie uzyskuje dochodów?",
          required: true,
          options: YES_NO,
        },
        {
          code: "przedsiebiorca",
          type: "radio",
          label: "Czy prowadzisz działalność gospodarczą?",
          hint: "Ulga dla przedsiębiorcy może stanowić pomoc de minimis (art. 59 ust. 2 ustawy o finansach publicznych).",
          required: true,
          options: YES_NO,
        },
      ],
    },
    {
      code: "oswiadczenia",
      title: "Oświadczenia",
      fields: [
        {
          code: "uznanieDlugu",
          type: "declaration",
          label:
            "Oświadczam, że w pełni uznaję swój dług z tytułu czynszu (odszkodowania za bezumowne korzystanie z lokalu) i opłat niezależnych od właściciela wraz z odsetkami, w wysokości wynikającej z ewidencji wynajmującego, oraz jestem świadoma/świadomy zaistniałych kosztów procesowych i komorniczych.",
          required: true,
        },
        {
          code: "terminoweWplaty",
          type: "declaration",
          label:
            "Jestem świadoma/świadomy, że warunkiem obowiązywania ulgi jest terminowe regulowanie rat oraz bieżących opłat za używanie lokalu. W razie braku wpłaty raty w terminie albo nieregulowania bieżących należności porozumienie przestaje obowiązywać, a pozostała należność staje się natychmiast wymagalna wraz z odsetkami.",
          required: true,
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    {
      code: "dochodyDokumenty",
      label: "Dokumenty potwierdzające dochody wszystkich osób zamieszkujących w lokalu za 3 miesiące",
      hint: "Np. zaświadczenia od pracodawcy, decyzje ZUS/KRUS, decyzje ośrodka pomocy społecznej, zaświadczenia z urzędu pracy.",
      required: true,
      multiple: true,
    },
    {
      code: "bezDochoduOswiadczenie",
      label: "Oświadczenie o nieuzyskiwaniu dochodów — podpisane przez osobę, która nie uzyskuje dochodów",
      visibleWhen: { field: "bezDochodu", equals: "tak" },
      required: true,
      multiple: true,
    },
    {
      code: "oswiadczenieMajatkowe",
      label: "Oświadczenie o sytuacji materialnej i majątkowej gospodarstwa domowego",
      hint: "Nieobowiązkowo — na druku urzędu, jeśli go wymaga.",
    },
    ...deMinimisAttachments(ENTREPRENEUR),
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default E3;
